import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import SEO from '../components/SEO';
import { User, Mail, Shield, Calendar, Edit3, Lock, Save, Trophy, Flame, Award, BookOpen, Brain, Star, Crown, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANK_CONFIG = {
  Explorer: { color: 'bg-slate-100 text-slate-700', icon: '1' },
  Apprenti: { color: 'bg-emerald-100 text-emerald-700', icon: '2' },
  Stratège: { color: 'bg-blue-100 text-blue-700', icon: '3' },
  Expert: { color: 'bg-purple-100 text-purple-700', icon: '4' },
  Master: { color: 'bg-amber-100 text-amber-700', icon: '5' },
};

export default function ProfilePage() {
  const { user, getAuthHeaders, checkAuth } = useAuth();
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit name
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState('');

  // Change password
  const [showPassword, setShowPassword] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/auth/profile/stats`, { headers: getAuthHeaders(), withCredentials: true });
        setStats(res.data);
      } catch {}
      setLoading(false);
    };
    fetchStats();
    if (user) setNewName(user.name || '');
  }, [user, getAuthHeaders]);

  const handleNameSave = async () => {
    if (!newName.trim() || newName.trim() === user?.name) { setEditingName(false); return; }
    setNameLoading(true);
    setNameMsg('');
    try {
      await axios.put(`${API}/auth/profile`, { name: newName.trim() }, { headers: getAuthHeaders(), withCredentials: true });
      setNameMsg(language === 'fr' ? 'Nom mis à jour' : 'Name updated');
      setEditingName(false);
      await checkAuth();
    } catch (err) {
      setNameMsg(err.response?.data?.detail || 'Error');
    }
    setNameLoading(false);
    setTimeout(() => setNameMsg(''), 3000);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      setPwdMsg({ type: 'error', text: language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match' });
      return;
    }
    if (newPwd.length < 6) {
      setPwdMsg({ type: 'error', text: language === 'fr' ? '6 caractères minimum' : 'Minimum 6 characters' });
      return;
    }
    setPwdLoading(true);
    setPwdMsg({ type: '', text: '' });
    try {
      await axios.put(`${API}/auth/password`, { current_password: currentPwd, new_password: newPwd }, { headers: getAuthHeaders(), withCredentials: true });
      setPwdMsg({ type: 'success', text: language === 'fr' ? 'Mot de passe modifié avec succès' : 'Password changed successfully' });
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      setShowPassword(false);
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
    setPwdLoading(false);
    setTimeout(() => setPwdMsg({ type: '', text: '' }), 4000);
  };

  const isGoogleUser = user?.picture && !user?.password_hash;
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
  const planLabel = {
    none: language === 'fr' ? 'Aucun plan actif' : 'No active plan',
    decouverte: 'IA Starter (Gratuit)',
    essentiel: 'IA Fondations (19€/mois)',
    pro: 'IA Pro (49€/mois)',
    entreprise: 'IA Expert (99€/pers/mois)',
  };
  const rankCfg = RANK_CONFIG[stats?.rank] || RANK_CONFIG.Explorer;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="profile-page">
      <SEO
        title={language === 'fr' ? 'Mon Profil' : 'My Profile'}
        description="Gérez votre profil IA Academy"
        path="/profile"
        noIndex
      />

      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Poppins' }} data-testid="profile-title">
          {language === 'fr' ? 'Mon Profil' : 'My Profile'}
        </h1>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Left Column - User Info */}
          <div className="md:col-span-1 space-y-6">
            {/* Avatar & Info Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="profile-info-card">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-white">{(user?.name || '?')[0].toUpperCase()}</span>
                )}
              </div>

              {editingName ? (
                <div className="flex items-center gap-2 justify-center mb-2" data-testid="edit-name-form">
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-name-input"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
                  />
                  <Button size="sm" onClick={handleNameSave} disabled={nameLoading} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="save-name-btn">
                    <Save className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-slate-900" data-testid="profile-name">{user?.name}</h2>
                  <button onClick={() => setEditingName(true)} className="text-slate-400 hover:text-blue-600 transition-colors" data-testid="edit-name-btn">
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
              )}
              {nameMsg && <p className="text-xs text-emerald-600 mb-2">{nameMsg}</p>}

              <div className="flex items-center justify-center gap-1.5 text-sm text-slate-500 mb-1">
                <Mail className="w-3.5 h-3.5" />
                <span data-testid="profile-email">{user?.email}</span>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-sm text-slate-500 mb-3">
                <Calendar className="w-3.5 h-3.5" />
                <span data-testid="profile-member-since">{memberSince}</span>
              </div>

              <Badge className={`${rankCfg.color} text-xs`} data-testid="profile-rank">
                <Crown className="w-3 h-3 mr-1" />
                {stats?.rank || 'Explorer'}
              </Badge>
            </div>

            {/* Plan Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="profile-plan-card">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-slate-900">{language === 'fr' ? 'Abonnement' : 'Subscription'}</h3>
              </div>
              <p className="text-lg font-bold text-slate-900" data-testid="profile-plan">{planLabel[user?.plan] || planLabel.none}</p>
              {user?.subscription?.expires_at && (
                <p className="text-xs text-slate-500 mt-1">
                  {language === 'fr' ? 'Expire le' : 'Expires'} {new Date(user.subscription.expires_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Stats & Security */}
          <div className="md:col-span-2 space-y-6">
            {/* Stats Grid */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="profile-stats-card">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-orange-500" />
                {language === 'fr' ? 'Statistiques' : 'Statistics'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <StatItem icon={Star} color="text-amber-500" bg="bg-amber-50" label={language === 'fr' ? 'Niveau' : 'Level'} value={stats?.level || 1} testId="stat-level" />
                <StatItem icon={Brain} color="text-purple-500" bg="bg-purple-50" label="XP" value={stats?.xp || 0} testId="stat-xp" />
                <StatItem icon={Flame} color="text-orange-500" bg="bg-orange-50" label={language === 'fr' ? 'Streak actuel' : 'Current Streak'} value={`${stats?.current_streak || 0}j`} testId="stat-streak" />
                <StatItem icon={BookOpen} color="text-blue-500" bg="bg-blue-50" label={language === 'fr' ? 'Modules terminés' : 'Modules Done'} value={stats?.modules_completed || 0} testId="stat-modules" />
                <StatItem icon={CheckCircle} color="text-emerald-500" bg="bg-emerald-50" label={language === 'fr' ? 'Quiz réussis' : 'Quizzes Done'} value={stats?.quizzes_completed || 0} testId="stat-quizzes" />
                <StatItem icon={Award} color="text-rose-500" bg="bg-rose-50" label="Badges" value={stats?.badges_earned || 0} testId="stat-badges" />
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-xs text-slate-500">{language === 'fr' ? 'Score moyen quiz' : 'Avg Quiz Score'}</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-avg-score">{stats?.avg_quiz_score || 0}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">{language === 'fr' ? 'Plus long streak' : 'Longest Streak'}</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-longest-streak">{stats?.longest_streak || 0} {language === 'fr' ? 'jours' : 'days'}</p>
                </div>
              </div>
            </div>

            {/* Security - Change Password */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="profile-security-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-slate-600" />
                  {language === 'fr' ? 'Sécurité' : 'Security'}
                </h3>
                {!isGoogleUser && !showPassword && (
                  <Button variant="outline" size="sm" onClick={() => setShowPassword(true)} data-testid="change-password-btn">
                    {language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
                  </Button>
                )}
              </div>

              {isGoogleUser ? (
                <p className="text-sm text-slate-500">
                  {language === 'fr'
                    ? 'Vous êtes connecté via Google. La gestion du mot de passe se fait sur votre compte Google.'
                    : 'You are logged in via Google. Password management is handled by your Google account.'}
                </p>
              ) : showPassword ? (
                <form onSubmit={handlePasswordChange} className="space-y-3" data-testid="password-form">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      {language === 'fr' ? 'Mot de passe actuel' : 'Current Password'}
                    </label>
                    <input
                      type="password"
                      value={currentPwd}
                      onChange={(e) => setCurrentPwd(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                    </label>
                    <input
                      type="password"
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
                    </label>
                    <input
                      type="password"
                      value={confirmPwd}
                      onChange={(e) => setConfirmPwd(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {pwdMsg.text && (
                    <p className={`text-xs ${pwdMsg.type === 'error' ? 'text-red-600' : 'text-emerald-600'}`} data-testid="password-message">
                      {pwdMsg.text}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={pwdLoading} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="submit-password-btn">
                      {pwdLoading
                        ? (language === 'fr' ? 'Modification...' : 'Changing...')
                        : (language === 'fr' ? 'Confirmer' : 'Confirm')}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => { setShowPassword(false); setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); }} data-testid="cancel-password-btn">
                      {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </Button>
                  </div>
                </form>
              ) : (
                <p className="text-sm text-slate-500">
                  {language === 'fr' ? 'Votre mot de passe est sécurisé.' : 'Your password is secure.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatItem({ icon: Icon, color, bg, label, value, testId }) {
  return (
    <div className="text-center p-3 rounded-xl bg-slate-50" data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mx-auto mb-2`}>
        <Icon className={`w-4.5 h-4.5 ${color}`} />
      </div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
